// src/components/email/EmailThread.tsx
import React from "react";
import {
  Box,
  Paper,
  Divider,
  Typography,
  Avatar,
  Stack,
  Chip,
} from "@mui/material";
import { ImapEmail } from "../../../types/imapEmail";
import EmailHtmlViewer from "./EmailHtmlViewer";
import InlineEmailHtmlContent from "./InlineEmailHtmlContent";

// Helper to extract the sender's name.
const getSenderName = (email: ImapEmail): string => {
  if (typeof email.from === "object") {
    return email.from.name || email.from.email || "Unknown";
  }
  return email.from || "Unknown";
};

// Helper to extract the sender's email.
const getSenderEmail = (email: ImapEmail): string => {
  if (typeof email.from === "object" && email.from.email) {
    return email.from.email;
  }
  return typeof email.from === "string" ? email.from : "Unknown";
};

// Helper to check if a string is a URL.
const isUrl = (str: string): boolean => {
  return /^https?:\/\//i.test(str);
};

interface EmailThreadProps {
  /**
   * Array of all emails in the same conversation (thread).
   */
  thread: ImapEmail[];
}

const EmailThread: React.FC<EmailThreadProps> = ({ thread }) => {
  // Sort the thread chronologically (oldest first).
  const sortedThread = [...thread].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );

  // Compute unique participants.
  const participantsSet = new Set<string>();
  sortedThread.forEach(email => {
    if (typeof email.from === "object") {
      participantsSet.add(email.from.name || email.from.email || "Unknown");
    } else {
      participantsSet.add(email.from || "Unknown");
    }
    if (email.to && Array.isArray(email.to)) {
      email.to.forEach(recipient => participantsSet.add(recipient));
    }
  });
  const participants = Array.from(participantsSet);

  return (
    <Box
      sx={{
        flex: 1,
        backgroundColor: "background.default",
        position: "relative",
      }}
    >
      {/* Sticky Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 2,
          backgroundColor: "background.default",
          borderBottom: "1px solid",
          borderColor: "divider",
          p: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: "bold", mb: 1 }}
        >
          Participants
        </Typography>
        {/* Display unique participants */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {participants.map((participant, idx) => (
            <Chip key={idx} label={participant} />
          ))}
        </Box>
      </Box>

      {/* Conversation messages */}
      <Box sx={{ p: 3 }}>
        {sortedThread.map((email, index) => {
          const senderName = getSenderName(email);
          const senderEmail = getSenderEmail(email);
          const avatarLetter = senderName.charAt(0).toUpperCase();
          const dateString = new Date(email.date).toLocaleString();
          const label = index === 0 ? "Original Message" : `Reply #${index}`;

          return (
            <Paper
              key={email.id}
              elevation={2}
              sx={{
                p: 2,
                mb: 2,
                backgroundColor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar sx={{ bgcolor: "primary.main", width: 44, height: 44 }}>
                  {avatarLetter}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                    {senderName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {senderEmail}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {dateString}
                  </Typography>
                </Box>
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Typography
                variant="subtitle2"
                sx={{ fontWeight: "bold", mb: 1 }}
                color="text.secondary"
              >
                {label}
              </Typography>

              {/* Render HTML content if available */}
              {email.html ? (
                isUrl(email.html) ? (
                  <InlineEmailHtmlContent url={email.html} />
                ) : (
                  <EmailHtmlViewer htmlContent={email.html} />
                )
              ) : (
                <Typography variant="body1" sx={{ whiteSpace: "pre-line" }}>
                  {email.body || "No content available."}
                </Typography>
              )}

              {email.hasAttachments && (
                <Chip
                  label="Contains attachments"
                  size="small"
                  color="secondary"
                  sx={{ mt: 2 }}
                />
              )}
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
};

export default EmailThread;
